import { FC, useCallback, useEffect, useRef } from 'react';

import { Button, Col, Input, Row, Typography } from 'antd';
import { ErrorMessage, Formik, useFormikContext } from 'formik';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

import * as S from './styled';

import DynamicPricingForm from '../DynamicPricingForm/DynamicPricingForm';
import {
  FixedPowerRow,
  MODULATION_STATIC,
  fixedPowerErrors,
  fixedPowerRows,
  modulationPayload,
} from '../SmartChargingForm/fixedPower';
import SmartChargingForm from '../SmartChargingForm/SmartChargingForm';
import { SimChargeBox } from '../SmartChargingSimulator/scenario';

export type VirtualGroupFormProps = {
  initialValues: any;
  loading: boolean;
  chargePointsList: any;
  onSubmit: any;
};

export enum VoucherExpireType {
  ExpirationDate = 'EXPIRATION_DATE',
  UsesPerUser = 'USES_PER_USER',
}

const UpdateChargeboxes = () => {
  const { setFieldValue, setValues } = useFormikContext();
  const { state } = useLocation<any>();

  useEffect(() => {
    if (state?.payload) {
      setValues(state.payload);

      setFieldValue('charge_boxs', state?.payload?.charge_boxs);
    }
  }, [setValues, setFieldValue, state]);

  return null;
};

const VirtualGroupForm: FC<VirtualGroupFormProps> = ({
  initialValues,
  loading,
  onSubmit,
}) => {
  const history = useHistory();
  const { state } = useLocation<any>();

  const user = JSON.parse(localStorage.getItem('auth-user'));

  // MV2-2005: conectores das estações selecionadas, para validar e enviar a potência fixa
  const fixedRows = useRef<FixedPowerRow[]>([]);
  const handleChargeBoxesChange = useCallback(
    (chargeBoxes: SimChargeBox[]) => {
      fixedRows.current = fixedPowerRows(chargeBoxes);
    },
    [],
  );

  const shouldShowSmartCharging = user?.permissions.some(
    (permission) =>
      permission.role_id === 'DASH_SMART_CHARGING_CREATE' &&
      !!permission.status,
  );

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validate={(values) => {
        const errors: any = {};
        let hasError = false;

        if (!values.name) {
          hasError = true;
          errors.name = 'O nome da configuração é um campo obrigatório';
        }

        if (!values.description) {
          hasError = true;
          errors.description = 'A descrição é um campo obrigatório';
        }

        if (values.dynamic_pricing.enabled) {
          errors.dynamic_pricing = {};

          if (
            values.dynamic_pricing.type === 'station' &&
            !values.dynamic_pricing.connector1_dynamic_pricing_id
          ) {
            hasError = true;

            errors.dynamic_pricing.connector1_dynamic_pricing_id =
              'Selecione um perfil de precificação';
          }

          if (values.dynamic_pricing.type === 'connectors') {
            if (!values.dynamic_pricing.connector1_dynamic_pricing_id) {
              hasError = true;
              errors.dynamic_pricing.connector1_dynamic_pricing_id =
                'Selecione um perfil de precificação';
            }
            if (!values.dynamic_pricing.connector2_dynamic_pricing_id) {
              hasError = true;
              errors.dynamic_pricing.connector2_dynamic_pricing_id =
                'Selecione um perfil de precificação';
            }
            if (!values.dynamic_pricing.connector3_dynamic_pricing_id) {
              hasError = true;
              errors.dynamic_pricing.connector3_dynamic_pricing_id =
                'Selecione um perfil de precificação';
            }
          }
        }

        if (values.smart_charging.enabled) {
          errors.smart_charging = {};

          if (!values.smart_charging.start_date) {
            hasError = true;
            errors.smart_charging.start_date =
              'A data de início é um campo obrigatório';
          }

          if (!values.smart_charging.end_date) {
            hasError = true;
            errors.smart_charging.end_date =
              'A data de término é um campo obrigatório';
          }

          if (
            moment(values.smart_charging.start_date).isAfter(
              values.smart_charging.end_date,
            )
          ) {
            hasError = true;
            errors.smart_charging.start_date =
              'A data de início deve ser anterior  a data final';
          }

          if (!values.smart_charging.current_nominal_a) {
            hasError = true;
            errors.smart_charging.current_nominal_a =
              'A corrente nominal do disjuntor (a) é um campo obrigatório';
          }

          if (!values.smart_charging.algorithm) {
            hasError = true;
            errors.smart_charging.algorithm =
              'O algoritmo é um campo obrigatório';
          }
          if (values.smart_charging.modulation === MODULATION_STATIC) {
            const powerErrors = fixedPowerErrors(
              values.smart_charging.algorithm,
              values.smart_charging.current_nominal_a,
              fixedRows.current,
              values.smart_charging.connector_powers,
            );
            if (powerErrors.length > 0) {
              hasError = true;
              errors.smart_charging.connector_powers = `Potência fixa: ${powerErrors.join(
                '; ',
              )}`;
            }
          }
        }

        return hasError ? errors : {};
      }}
      onSubmit={(values, helpers) =>
        onSubmit(
          {
            ...values,
            smart_charging: {
              ...values.smart_charging,
              ...modulationPayload(values.smart_charging, fixedRows.current),
            },
          },
          helpers,
        )
      }
    >
      {({ handleSubmit, handleChange, values }) => (
        <>
          <form onSubmit={handleSubmit}>
            <Row gutter={24}>
              <Col md={24}>
                <Typography.Title level={5}>Informações</Typography.Title>
              </Col>
            </Row>
            <Row gutter={24} style={{ marginTop: 8 }}>
              <Col md={12}>
                <S.Label>Nome da configuração</S.Label>
                <Input
                  name='name'
                  size='large'
                  disabled={loading}
                  onChange={handleChange}
                  maxLength={30}
                  value={values.name}
                />
                <ErrorMessage name='name' component={S.MessageBox} />
              </Col>

              <Col md={12}>
                <S.Label>Descrição</S.Label>
                <Input
                  name='description'
                  size='large'
                  disabled={loading}
                  onChange={handleChange}
                  maxLength={50}
                  value={values.description}
                />
                <ErrorMessage name='description' component={S.MessageBox} />
              </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 24 }}>
              <Col md={24}>
                <Typography.Title level={5}>Carregadores</Typography.Title>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col md={24} style={{ display: 'flex', alignItems: 'center' }}>
                {state?.payload?.charge_boxs && (
                  <>
                    <S.ChargeboxCircle>
                      {state?.payload?.charge_boxs?.length}
                    </S.ChargeboxCircle>

                    <S.ChargeboxText>
                      carregador
                      {state?.payload?.charge_boxs?.length > 1 ? 'es' : ''}
                    </S.ChargeboxText>
                  </>
                )}

                <Typography.Title
                  level={5}
                  style={{ fontWeight: '300', marginBottom: 0 }}
                >
                  <Button
                    size='large'
                    type='link'
                    style={{ padding: 0 }}
                    disabled={loading}
                    onClick={() =>
                      history.push({
                        pathname: '/virtual-groups/chargeboxes/select',
                        state: {
                          payload: values,
                          origin: '/virtual-groups/create',
                        },
                      })
                    }
                  >
                    <S.Link>Selecionar carregadores</S.Link>
                  </Button>
                </Typography.Title>
              </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 24 }}>
              <Col md={24}>
                <Typography.Title level={5}>
                  Perfil de precificação
                </Typography.Title>
              </Col>
            </Row>

            <DynamicPricingForm loading={loading} />

            {!!shouldShowSmartCharging && (
              <SmartChargingForm
                loading={loading}
                onChargeBoxesChange={handleChargeBoxesChange}
              />
            )}

            <Row style={{ marginTop: 32, justifyContent: 'space-between' }}>
              <S.Button
                disabled={loading}
                size='large'
                danger
                onClick={history.goBack}
              >
                Cancelar
              </S.Button>

              <S.Button
                loading={loading}
                key='submit'
                size='large'
                type='primary'
                htmlType='submit'
              >
                Criar Configuração
              </S.Button>
            </Row>
          </form>
          <UpdateChargeboxes />
        </>
      )}
    </Formik>
  );
};

export default VirtualGroupForm;
